import {useEffect, useState, useContext, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate, useLocation} from 'react-router-dom';
import _filter from 'lodash/filter';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import utils from '../../helpers/utils';
import Dropdown from '../../components/common/Dropdown';
import {IntlContext} from '../../containers/IntlProvider';
import * as menuUtils from '../../helpers/menuUtils';
import {RootState} from '../../reducers/rootReducer';
import {useRestorationCases} from '../../customHooks/useRestoration';
import {useScamVerificationCases} from '../../customHooks/useScamVerification';
import useWhiteLabelClient from '../../customHooks/useWhiteLabelClient';
import {useSmmAccounts} from '../../customHooks/useSmmAccounts';
import {usePrivacyMonitorLicense} from '../../customHooks/usePrivacyMonitor';
import {useFinancialOffers} from '../../customHooks/useFinancialOffers';
import {useConnectionsDetails} from '../../customHooks/useConnectionsDetails';
import classNames from 'classnames';
import * as partnerUtils from '../../helpers/partnerUtils';

const MenuMobile = () => {
  const localizedStringBundle: StringMap = useContext(IntlContext);
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const store = useSelector((store) => store);
  const whiteLabelClient = useWhiteLabelClient();
  const productFeatures = utils.safeGet(store, 'auth.user.primaryMember.plan.productFeatures', {});
  const marketingDetails = utils.safeGet(store, 'auth.user.primaryMember.plan.marketingDetails', {});
  const alertListInbox = useSelector((store: RootState) => utils.safeGet(store, 'alertList.alertListInbox', []));
  const institutionsList = useSelector((store: RootState) => utils.safeGet(store, 'transactions.institutionsList', []));
  const accountId = utils.safeGet(store, 'auth.user.primaryMember.accountId');
  const {data: connectionsResponse} = useConnectionsDetails(accountId, productFeatures);
  const connectionsData = utils.safeGet(connectionsResponse, 'connections', []);
  const auth = useSelector((store: RootState) => store.auth);
  const userCountryCode = utils.safeGet(auth, 'user.countryCode', '');
  const {data: privacyLicense} = usePrivacyMonitorLicense(userCountryCode);
  const {data: smmAccounts} = useSmmAccounts(accountId);
  const {data: restorationCases} = useRestorationCases(accountId);
  const {data: scamVerificationCases} = useScamVerificationCases(productFeatures);
  const {isMLCardOffersActive, isCreditOffersMarketplaceActive, isSavingsOffersMarketplaceActive} = useFinancialOffers(
    productFeatures,
    localizedStringBundle
  );
  const transactions = useSelector((store: RootState) => store.transactions);
  const showWarningIconUnified = utils.safeGet(transactions, 'showWarningIconUnified', false);

  const dynamicMenuData = useMemo(
    () => ({
      alertListInbox,
      institutionsList: utils.showUnifiedComponents({productFeatures}) ? connectionsData : institutionsList,
      restorationCases,
      scamVerificationCases,
      smmAccounts,
      showWarningIconUnified,
    }),
    [
      alertListInbox,
      institutionsList,
      restorationCases,
      scamVerificationCases,
      smmAccounts,
      showWarningIconUnified,
      connectionsData,
      productFeatures,
    ]
  );

  const list = utils.dspMenuList(
    productFeatures,
    marketingDetails,
    dynamicMenuData,
    whiteLabelClient,
    isMLCardOffersActive,
    privacyLicense,
    isCreditOffersMarketplaceActive || isSavingsOffersMarketplaceActive
  );
  const filteredList = _filter(
    list,
    (v) => pathname === v.value || pathname.startsWith(v.value + '/') || pathname.startsWith(v.value + '?')
  );

  const defaultOption = () => {
    return {
      value: '/dashboard',
      name: localizedStringBundle.LEFT_NAV_DASHBOARD,
      option: {category: 'Navigation', action: 'click', label: 'Dashboard Tab'},
      iconName: 'Dashboard',
      supportComponent: '',
    };
  };

  const [selected, setSelected] = useState(filteredList[0] || defaultOption());
  const localizedList = _isEmpty(localizedStringBundle)
    ? []
    : list.map((item) => {
        return {
          value: item.value,
          name: localizedStringBundle[utils.safeGet(item, 'name', '')],
          logo: menuUtils.getPathForNavIcon(item.iconName, whiteLabelClient),
          supportComponent: item.supportComponent || '',
          iconName: item.iconName,
          showIconBadge: item.showIconBadge,
        };
      });

  let listFiltered = localizedList;
  const selectedLabel =
    localizedStringBundle[utils.safeGet(selected, 'name', '')] || utils.safeGet(selected, 'name', '');
  const selectedObj = {value: utils.safeGet(selected, 'value', ''), name: selectedLabel};

  useEffect(() => {
    const filteredList = _filter(
      list,
      (v) => pathname === v.value || pathname.startsWith(v.value + '/') || pathname.startsWith(v.value + '?')
    );

    if (_isEmpty(filteredList)) {
      filteredList[0] = defaultOption();
    }

    if (pathname.indexOf('online-account-monitoring') === 1) {
      setSelected({
        name: 'MONITORING',
        value: '/online-account-monitoring',
        option: {category: 'Navigation', action: 'click', label: 'Monitored Info Tab'},
        iconName: 'MonitoredInfo',
        supportComponent: '',
      });
      listFiltered = localizedList;
    } else if (!_isEqual(filteredList[0], selected)) {
      setSelected(filteredList[0]);
    }
  }, [pathname]);

  const headerTitle = utils.getHeaderTitleString(
    productFeatures,
    marketingDetails,
    localizedStringBundle,
    whiteLabelClient
  );
  const hasFixedMobileMenu: boolean = partnerUtils.hasFixedMobileMenu(whiteLabelClient);
  const dropdownContClasses = classNames(
    {'fixed left-0 right-0 top-0': hasFixedMobileMenu},
    'flex flex-nowrap justify-between border-1 border-onBackground-light bg-background'
  );

  return (
    <div className={dropdownContClasses}>
      <Dropdown
        menuMobile={true}
        list={listFiltered}
        key="menu-dropdown"
        selectedName={selectedObj}
        setSelected={(item) => {
          navigate(item.value);
          setSelected(item);
        }}
        headerTitle={headerTitle}
        whiteLabelClient={whiteLabelClient}
      />
    </div>
  );
};

export default MenuMobile;
